import { Workbox } from 'workbox-window';

export interface CacheConfig {
  key: string;
  fetchFn: () => Promise<any>;
  maxAge?: number; // in seconds
  forceRefresh?: boolean;
}

export interface CachedData<T = any> {
  data: T;
  timestamp: number;
  key: string;
}

class CacheService {
  private wb: Workbox | null = null;
  private swRegistration: ServiceWorkerRegistration | null = null;
  private messageCallbacks: Map<string, (data: any) => void> = new Map();

  constructor() {
    // Only register the Workbox service worker in production builds — in dev
    // (Vite) it intercepts and can serve stale cached responses for the app's
    // own module/script requests, breaking HMR and masking code changes.
    if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
      this.wb = new Workbox('/sw.js');
      this.initializeServiceWorker();
    }
  }

  private async initializeServiceWorker() {
    if (!this.wb) return;

    // Listen for messages from service worker
navigator.serviceWorker.addEventListener('message', (event) => {
  const { type, payload } = event.data;
  
  switch (type) {
    case 'DATA_CACHED':
      console.log('Data successfully cached:', payload.key);
      break;
    case 'CACHED_DATA_RESPONSE': {
      const callback = this.messageCallbacks.get(payload.key);
      if (callback) {
        callback(payload);
        this.messageCallbacks.delete(payload.key);
      }
      break;
    }
  }
});

    try {
      this.swRegistration = await this.wb.register() || null;
      console.log('Service Worker registered successfully');
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  }

  /**
   * Cache-first strategy: Try to get data from cache first, then network
   * If cache is stale, fetch from network in background and update cache
   */
  async getCachedData<T>(config: CacheConfig): Promise<T> {
    const { key, fetchFn, maxAge = 300, forceRefresh = false } = config; // 5 minutes default

    console.log(`[CACHE] Getting data for key: ${key}`);

    // If force refresh, skip cache and fetch from network
    if (forceRefresh) {
      console.log(`[CACHE] Force refresh requested for ${key}`);
      return await this.fetchAndCache(key, fetchFn);
    }

    // Try to get from cache first
    const cachedData = await this.getFromCache<T>(key);
    
    if (cachedData) {
      const age = (Date.now() - cachedData.timestamp) / 1000; // in seconds
      console.log(`[CACHE] Found cached data for ${key}, age: ${age}s`);
      
      // If cache is still fresh, return it
      if (age < maxAge) {
        console.log(`[CACHE] Cache is fresh, returning cached data for ${key}`);
        return cachedData.data;
      }
      
      // Cache is stale, return cached data but fetch new data in background
      console.log(`[CACHE] Cache is stale for ${key}, fetching new data in background`);
      this.fetchAndCache(key, fetchFn).catch(error => {
        console.error(`[CACHE] Background fetch failed for ${key}:`, error);
      });
      
      return cachedData.data;
    }

    // No cache found, fetch from network
    console.log(`[CACHE] No cache found for ${key}, fetching from network`);
    return await this.fetchAndCache(key, fetchFn);
  }

  /**
   * Network-first strategy: Try network first, fallback to cache
   */
  async getNetworkFirst<T>(config: CacheConfig): Promise<T> {
    const { key, fetchFn } = config;

    try {
      console.log(`[CACHE] Network-first: Trying network for ${key}`);
      return await this.fetchAndCache(key, fetchFn);
    } catch (error) {
      console.log(`[CACHE] Network failed for ${key}, trying cache`);
      const cachedData = await this.getFromCache<T>(key);
      
      if (cachedData) {
        console.log(`[CACHE] Returning stale cache for ${key}`);
        return cachedData.data;
      }
      
      throw error;
    }
  }

  /**
   * Stale-while-revalidate: Return cache immediately if available, 
   * then fetch new data and update cache
   */
  async getStaleWhileRevalidate<T>(config: CacheConfig): Promise<T> {
    const { key, fetchFn } = config;

    // Get cached data immediately
    const cachedData = await this.getFromCache<T>(key);
    
    // Fetch new data in background
    this.fetchAndCache(key, fetchFn).catch(error => {
      console.error(`[CACHE] Background fetch failed for ${key}:`, error);
    });

    // Return cached data if available, otherwise wait for network
    if (cachedData) {
      console.log(`[CACHE] Returning cached data for ${key}, updating in background`);
      return cachedData.data;
    }

    console.log(`[CACHE] No cache available for ${key}, waiting for network`);
    return await this.fetchAndCache(key, fetchFn);
  }

  private async fetchAndCache<T>(key: string, fetchFn: () => Promise<T>): Promise<T> {
    try {
      const data = await fetchFn();
      await this.setCache(key, data);
      return data;
    } catch (error) {
      console.error(`[CACHE] Fetch failed for ${key}:`, error);
      throw error;
    }
  }

  /**
   * Public method to check if data exists in cache
   */
  async hasCache(key: string): Promise<boolean> {
    const cachedData = await this.getFromCache(key);
    return cachedData !== null;
  }

  private async getFromCache<T>(key: string): Promise<CachedData<T> | null> {
    return new Promise((resolve) => {
      // Use a unique message ID to handle concurrent requests
      const messageId = `${key}_${Date.now()}_${Math.random()}`;
      
      this.messageCallbacks.set(messageId, (payload) => {
        if (payload.found) {
          resolve({
            data: payload.data.data,
            timestamp: payload.data.timestamp,
            key: payload.data.key,
          });
        } else {
          resolve(null);
        }
      });

      // Send message to service worker to get cached data
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'GET_CACHED_DATA',
          payload: { key, messageId },
        });
      } else {
        resolve(null);
      }

      // Timeout after 3 seconds for slower devices
      setTimeout(() => {
        if (this.messageCallbacks.has(messageId)) {
          this.messageCallbacks.delete(messageId);
          resolve(null);
        }
      }, 3000);
    });
  }

  private async setCache<T>(key: string, data: T): Promise<void> {
    const cachedData: CachedData<T> = {
      data,
      timestamp: Date.now(),
      key,
    };

    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CACHE_APP_DATA',
        payload: { key, data: cachedData },
      });
    }
  }

  /**
   * Clear specific cache entry
   */
  async clearCache(key: string): Promise<void> {
    if ('caches' in window) {
      const cache = await caches.open('app-data-v1');
      await cache.delete(key);
    }
  }

  /**
   * Clear all app data cache
   */
  async clearAllCache(): Promise<void> {
    if ('caches' in window) {
      await caches.delete('app-data-v1');
    }
  }

  /**
   * Get cache info (size, entries, etc.)
   */
  async getCacheInfo(): Promise<{ keys: string[]; size: number }> {
    if (!('caches' in window)) {
      return { keys: [], size: 0 };
    }

    try {
      const cache = await caches.open('app-data-v1');
      const keys = await cache.keys();

      let totalSize = 0;
      for (const request of keys) {
        const response = await cache.match(request);
        if (response) {
          const sizeHeader = response.headers.get('content-length');
          const size = sizeHeader
            ? parseInt(sizeHeader, 10)
            : (await response.clone().blob()).size;
          totalSize += size || 0;
        }
      }

      return {
        keys: keys.map(req => req.url),
        size: totalSize,
      };
    } catch (error) {
      console.error('Error getting cache info:', error);
      return { keys: [], size: 0 };
    }
  }

  /**
   * Check if service worker is supported and registered
   */
  isServiceWorkerSupported(): boolean {
    return 'serviceWorker' in navigator && this.swRegistration !== null;
  }

  /**
   * Check if the app is offline
   */
  isOffline(): boolean {
    return !navigator.onLine;
  }

  /**
   * Listen for online/offline events
   */
  onConnectivityChange(callback: (isOnline: boolean) => void): () => void {
    const handleOnline = () => callback(true);
    const handleOffline = () => callback(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Return cleanup function
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }
}

// Create singleton instance
export const cacheService = new CacheService();

// React hook for using cache service
export const useCacheService = () => {
  return cacheService;
}; 